import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { FaBook, FaBars, FaTimes, FaApple, FaGooglePlay, FaArrowRight } from 'react-icons/fa';

const APP_STORE_URL = 'https://apps.apple.com/us/app/pasur-online-play-score/id6476008595';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.vakilventures.pasur';

const HeaderContainer = styled.header<{ scrolled: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem 2rem;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 72px;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  background: rgba(10, 22, 40, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);

  ${props => props.scrolled && css`
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.15);
  `}

  @media (max-width: 768px) {
    padding: 1rem 1.25rem;
    height: 64px;
  }
`;

const Logo = styled(Link)`
  font-family: 'Outfit', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  letter-spacing: -0.02em;
  transition: all 0.3s ease;

  &:hover {
    color: var(--color-gold);
  }

  @media (min-width: 768px) {
    font-size: 1.6rem;
  }
`;


const Nav = styled.nav`
  display: none;
  align-items: center;
  gap: 0.5rem;

  @media (min-width: 768px) {
    display: flex;
  }
`;

const NavLink = styled(Link)<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.95rem;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
  }

  ${({ active }) => active && css`
    color: var(--color-gold);
  `}
`;

const DownloadButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  background: var(--color-gold);
  color: var(--color-midnight);
  border: none;
  border-radius: 8px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  margin-left: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(212, 165, 116, 0.35);
  }

  svg {
    font-size: 1.1rem;
  }
`;

const MobileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileDownloadPill = styled.a`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.85rem;
  background: var(--color-gold);
  color: var(--color-midnight);
  border-radius: 20px;
  text-decoration: none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
  transition: all 0.3s ease;

  svg {
    font-size: 0.95rem;
  }
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  svg {
    font-size: 1.2rem;
  }

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileMenu = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 360px;
  background: linear-gradient(180deg, #0a1628 0%, #0f1f3a 100%);
  box-shadow: -10px 0 40px rgba(0, 0, 0, 0.4);
  transform: translateX(${({ isOpen }) => (isOpen ? '0' : '100%')});
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 1001;
  display: flex;
  flex-direction: column;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MenuOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.4s ease, visibility 0.4s ease;
  z-index: 1000;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const MenuTitle = styled.span`
  font-family: 'Outfit', sans-serif;
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  cursor: pointer;
  border-radius: 10px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const MenuContent = styled.div`
  flex: 1;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MenuItem = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 1rem 1.25rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: white;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: var(--color-gold);
  }

  .icon-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 1rem 0;
`;

const StoreLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: auto;
`;

const StoreButton = styled.a`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: var(--color-gold);
  color: var(--color-midnight);
  border-radius: 12px;
  text-decoration: none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  transition: all 0.3s ease;

  &:first-child {
    background: white;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }

  svg {
    font-size: 1.4rem;
  }
`;

const isAndroid = () => /android/i.test(navigator.userAgent);

const Header: React.FC = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <HeaderContainer scrolled={scrolled}>
        <Logo to="/">
          Pasur
        </Logo>

        <Nav>
          <NavLink to="/rules" active={location.pathname === '/rules'}>
            <FaBook /> Rules
          </NavLink>
          <DownloadButton href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
            <FaApple /> Get the App
          </DownloadButton>
        </Nav>

        <MobileActions>
          <MobileDownloadPill
            href={isAndroid() ? PLAY_STORE_URL : APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {isAndroid() ? <FaGooglePlay /> : <FaApple />} Get App
          </MobileDownloadPill>
          <MenuButton
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </MenuButton>
        </MobileActions>
      </HeaderContainer>

      <MenuOverlay isOpen={menuOpen} onClick={() => setMenuOpen(false)} />
      <MobileMenu isOpen={menuOpen}>
        <MenuHeader>
          <MenuTitle>Menu</MenuTitle>
          <CloseButton onClick={() => setMenuOpen(false)} aria-label="Close menu">
            <FaTimes />
          </CloseButton>
        </MenuHeader>
        <MenuContent>
          <MenuItem to="/rules" onClick={() => setMenuOpen(false)}>
            <span className="icon-left">
              <FaBook /> Game Rules
            </span>
            <FaArrowRight style={{ opacity: 0.5 }} />
          </MenuItem>

          <MenuDivider />

          <StoreLinks>
            <StoreButton href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
              <FaApple /> Download on App Store
            </StoreButton>
            <StoreButton href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
              <FaGooglePlay /> Get it on Google Play
            </StoreButton>
          </StoreLinks>
        </MenuContent>
      </MobileMenu>
    </>
  );
};

export default Header;
